import {
  ImportStepper,
  defaultGenerateStepper,
  type ImportFlows,
} from '@backstage/plugin-catalog-import';
import Typography from '@material-ui/core/Typography';
import StepLabel from '@material-ui/core/StepLabel';
import CustomAnalyzeStepContent from './CustomAnalyzeStepContent';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  stepperWrapper: {
    '& [class*="MuiStepLabel-label"]': {
      ...theme.typography.h5,
      fontWeight: 500,
    },
    '& [class*="MuiTypography-root"][class*="MuiTypography-paragraph"]': {
      ...theme.typography.body2,
      fontWeight: 500,
    },
    '& [class*="MuiDivider-root"]': {
      display: 'none',
    },
    '& [class*="MuiStepIcon-text"]': {
      fontWeight: 'bold',
    },
    '& [class*="MuiListItem-root"]': {
      paddingLeft: theme.spacing(3),
    },
    '& [class*="MuiListItemIcon-root"]': {
      minWidth: 0,
      paddingRight: theme.spacing(0.5),
      color: theme.palette.text.primary,
    },
    '& [class*="CatalogReactEntityDisplayName-icon"]': {
      color: theme.palette.text.primary,
    },
    '& a [class*="CatalogReactEntityDisplayName-icon"]': {
      color: theme.palette.primary.main,
    },
    '& [class*="MuiPaper-elevation1"]': {
      boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.20)',
    },
  },
}));

const getPrepareStepLabelText = (
  flow: ImportFlows,
  state: any,
): { title: string; description: string | null } => {
  switch (flow) {
    case 'single-location':
      return {
        title: 'Prepare',
        description: 'Automatically selected the only configuration found',
      };
    case 'multiple-locations':
      return {
        title: 'Prepare - Multiple',
        description: `Discovered Locations: ${
          state?.analyzeResult?.locations?.length || 'Unknown'
        }`,
      };
    default:
      return {
        title: 'Prepare',
        description: null,
      };
  }
};

const customStepperBuilder = (flow: ImportFlows, defaults: any, t: any) => {
  const baseSteps = defaultGenerateStepper(flow, defaults, t);

  return {
    analyze: (state: any, _opts: any) => {
      return {
        stepLabel: <StepLabel>Enter the URL to an existing file</StepLabel>,
        content: (
          <CustomAnalyzeStepContent
            analysisUrl={state.analysisUrl}
            onAnalysis={state.onAnalysis}
          />
        ),
      };
    },

    prepare: (state: any, opts: any) => {
      const { content } = baseSteps.prepare(state, opts);
      const { title, description } = getPrepareStepLabelText(flow, state);
      return {
        stepLabel: (
          <StepLabel
            optional={
              description ? (
                <Typography variant="body2" color="textSecondary">
                  {description}
                </Typography>
              ) : null
            }
          >
            {title}
          </StepLabel>
        ),
        content,
      };
    },
    review: (state: any, opts: any) => {
      const { content } = baseSteps.review(state, opts);
      return {
        stepLabel: <StepLabel>Review and Import</StepLabel>,
        content,
      };
    },
    finish: baseSteps.finish,
  };
};

/**
 * A custom implementation of the ImportStepper with enhanced UI
 * This wraps the ImportStepper with additional context
 */
export const CustomImportStepper = () => {
  const classes = useStyles();
  return (
    <div className={classes.stepperWrapper}>
      <ImportStepper generateStepper={customStepperBuilder} />
    </div>
  );
};
